import { Fragment, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { renderTemplate, type OutreachStep } from '@shared/outreach';

export interface PreviewProspect {
  recordId: string;
  title: string;
  person: string;
  values: Record<string, string>;
}

/** Shows a missing parameter where it stands, so it is seen in place. */
const Assembled = ({ text }: { text: string }) => (
  <>
    {text.split(/(\{\{[a-zA-Z0-9_]+\}\})/g).map((part, index) => /^\{\{[a-zA-Z0-9_]+\}\}$/.test(part)
      ? <mark key={index} className="rounded bg-domino-plumTint px-1 text-domino-aubergine">{part}</mark>
      : <Fragment key={index}>{part}</Fragment>)}
  </>
);

/**
 * The e-mails as they will leave, assembled with one prospect's own values;
 * ‹ › goes from one prospect to the next. What a prospect lacks is shown in
 * place: « Préparer » leaves that prospect out until it is filled.
 */
export const OutreachPreview = ({ steps, prospects }: { steps: OutreachStep[]; prospects: PreviewProspect[] }) => {
  const [index, setIndex] = useState(0);
  if (prospects.length === 0) {
    return <div className="rounded-xl border border-dashed border-domino-cardBorder p-6 text-center text-sm text-domino-inkSoft">Aperçu après « Trouver qui contacter »</div>;
  }
  const at = Math.min(index, prospects.length - 1);
  const prospect = prospects[at]!;
  const subject = renderTemplate(steps[0]?.subject ?? '', prospect.values);
  const bodies = steps.map((step) => renderTemplate(step.body, prospect.values));
  const missing = [...new Set([...subject.missing, ...bodies.flatMap((body) => body.missing)])];

  return (
    <div className="space-y-3 rounded-xl border border-domino-cardBorder bg-domino-white p-4">
      <div className="flex items-center justify-between gap-2">
        <button type="button" aria-label="Prospect précédent" disabled={at === 0} onClick={() => setIndex(at - 1)}
          className="rounded-lg p-1 text-domino-inkSoft hover:bg-domino-plumTint disabled:opacity-30"><ChevronLeft className="h-4 w-4" /></button>
        <div className="min-w-0 text-center">
          <p className="truncate text-sm font-semibold text-domino-ink">{prospect.title}</p>
          <p className="truncate text-xs text-domino-inkSoft">{prospect.person} · {at + 1}/{prospects.length}</p>
        </div>
        <button type="button" aria-label="Prospect suivant" disabled={at === prospects.length - 1} onClick={() => setIndex(at + 1)}
          className="rounded-lg p-1 text-domino-inkSoft hover:bg-domino-plumTint disabled:opacity-30"><ChevronRight className="h-4 w-4" /></button>
      </div>
      {missing.length > 0 && <p className="text-xs text-domino-aubergine">Manque : {missing.join(', ')}</p>}
      {steps.map((_, stepIndex) => (
        <div key={stepIndex} className="space-y-1 border-t border-domino-cardBorder pt-3">
          <p className="text-xs font-medium text-domino-inkSoft">{stepIndex === 0 ? 'Premier e-mail' : `Relance ${stepIndex}`}</p>
          <p className="text-sm font-semibold text-domino-ink"><Assembled text={stepIndex === 0 ? subject.text : `Re: ${subject.text}`} /></p>
          <p className="whitespace-pre-wrap text-sm text-domino-ink"><Assembled text={bodies[stepIndex]!.text} /></p>
        </div>
      ))}
    </div>
  );
};
